<script setup lang="ts">
import InputError from '@/components/InputError.vue';
import InputLabel from '@/components/InputLabel.vue';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import CardContent from '@/components/ui/card/CardContent.vue';
import CardDescription from '@/components/ui/card/CardDescription.vue';
import CardFooter from '@/components/ui/card/CardFooter.vue';
import CardHeader from '@/components/ui/card/CardHeader.vue';
import CardTitle from '@/components/ui/card/CardTitle.vue';
import { Input } from '@/components/ui/input';
import { ref } from 'vue';
import { useForgotPassword } from '@/composables/api/useAuthApi';
import { AxiosError } from 'axios';

interface ForgotPasswordErrors {
  email?: string[];
  [key: string]: string[] | undefined;
}

const { mutate: forgotPassword, isPending: isSendingResetLink } = useForgotPassword();

const errors = ref<ForgotPasswordErrors>({});
const email = ref('');

function resetForm() {
  email.value = '';
  errors.value = {};
}

function handleSubmit() {
  forgotPassword(
    {
      email: email.value
    },
    {
      onSuccess: () => {
        resetForm();
      },
      onError: (error: unknown) => {
        // Clear any previous errors
        errors.value = {};
        
        // Only handle validation errors here, other errors are handled by composable
        if (error instanceof AxiosError && error.response?.status === 422) {
          const errorData = error.response.data;
          if (errorData.errors) {
            errors.value = errorData.errors;
          }
        }
      }
    }
  );
}
</script>

<template>
  <Card class="px-6 py-4">
    <form @submit.prevent="handleSubmit">
      <CardHeader>
        <CardTitle>Forgot Password</CardTitle>
        <CardDescription class="mt-2">
          Forgot your password? No problem. Just let us know your email address and we will email you
          a password reset link that will allow you to choose a new one.
        </CardDescription>
      </CardHeader>
      <CardContent class="mt-6">
        <div class="space-y-2">
          <InputLabel for="email" value="Email" />
          <Input 
            id="email" 
            type="email" 
            class="mt-1 block w-full" 
            required 
            autofocus 
            autocomplete="email"
            autocapitalize="none"
            spellcheck="false"
            name="email"
            v-model="email"
            :disabled="isSendingResetLink"
            :class="{ 'border-red-500': errors['email'] }"
          />
          <InputError
            v-if="errors['email']"
            :message="errors['email'][0]"
            class="mt-2"
          />
        </div>
      </CardContent>
      <CardFooter class="mt-6">
        <div class="flex justify-between items-center w-full">
          <RouterLink :to="{ name: 'login' }" custom v-slot="{ navigate }">
            <Button type="button" variant="link" :disabled="isSendingResetLink" @click="navigate">
              Back to Login
            </Button>
          </RouterLink>
          <Button 
          type="submit"
          :disabled="isSendingResetLink"
          :loading="isSendingResetLink"
          >
          Email Password Reset Link
        </Button>
      </div>
      </CardFooter>
    </form>
  </Card>
</template>
